import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, User, Phone, Check } from 'lucide-react';
import { TosModal, TosLink } from '../components/TosModal';
import axios from 'axios';

import API from '../utils/api';

// Plan picker shown above the sign-up form so users coming in from the
// landing Mastery Course cards can decide between free access (→ /courses)
// and paid subscription (→ Stripe checkout) before they register.
const PLAN_OPTIONS = [
  { id: 'free',     label: 'Free',     price: '$0',     blurb: 'Browse courses · 14-day calendar trial', paid: false },
  { id: 'basic',    label: 'Basic',    price: '$9.99',  blurb: 'Full AI communication · free co-parent messaging · plan builder', paid: true },
  { id: 'standard', label: 'Standard', price: '$24.99', blurb: 'All features unlocked · free co-parent messaging · 200 video min', paid: true },
  { id: 'pro',      label: 'Pro',      price: '$34.99', blurb: 'Everything unlimited · free co-parent messaging · 450 video min', paid: true },
];

const Signup = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fromCourses = params.get('from') === 'courses';
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tosOpen, setTosOpen] = useState(false);
  const [tosAccepted, setTosAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tosAccepted) {
      setTosOpen(true);
      return;
    }
    setError('');
    setLoading(true);

    // Log acceptance with IP before registering
    try {
      await axios.post(API + '/tos/accept-signup', { email: email, marketing_consent: marketingConsent });
    } catch (_) {
      /* non-fatal — ToS audit log is best-effort */
    }

    const result = await register(name, email, password, phone);
    if (!result.success) {
      setLoading(false);
      setError(result.error);
      return;
    }

    // Paid plan → kick off Stripe checkout straight from sign-up so the user
    // never lands on the dashboard without payment in flight.
    const planOpt = PLAN_OPTIONS.find(function (p) { return p.id === selectedPlan; });
    if (planOpt && planOpt.paid) {
      try {
        const r = await axios.post(API + '/billing/create-checkout', {
          plan: selectedPlan,
          interval: 'monthly',
          trial: true,
          origin_url: window.location.origin,
        }, { withCredentials: true });
        if (r.data && r.data.url) {
          window.location.href = r.data.url;
          return;
        }
        setError('Could not start checkout. Please try again from Billing.');
        setLoading(false);
        return;
      } catch (err) {
        setError((err.response && err.response.data && err.response.data.detail) || 'Could not start checkout. Please try again from Billing.');
        setLoading(false);
        return;
      }
    }

    setLoading(false);
    // Free plan → land on the courses page if they came in from there,
    // otherwise the dashboard.
    navigate(fromCourses ? '/courses' : '/dashboard');
  };

  const handleTosAccept = function (version, mktConsent) {
    setTosAccepted(true);
    setMarketingConsent(mktConsent || false);
    setTosOpen(false);
  };

  const handleGoogleSignup = () => {
    if (!tosAccepted) {
      setTosOpen(true);
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Reverted to Emergent-managed Google Auth until our own OAuth app passes Google verification.
    // The custom backend route /api/auth/google/start is still wired and ready to switch back.
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F6F7FC] flex items-center justify-center p-6">
      <div className="absolute inset-0 opacity-60 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#DDE1EE] to-[#A8AAD4] rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-[#A8AAD4] to-[#DDE1EE] rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="card-surface p-8">
          <div className="text-center mb-8">
            <h1 className="font-serif-display text-4xl font-bold mb-2 text-[#2D2560]">
              Parentum
            </h1>
            <p className="text-[#6B6690]">Create your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Plan picker — Free vs Paid. Free = redirect to /courses,
                Paid = jump straight to Stripe checkout after the account
                is created. Visible to every visitor; defaults to Free. */}
            <div data-testid="signup-plan-picker">
              <label className="block text-sm text-[#3A3566] font-medium mb-2">{fromCourses ? 'Pick a plan to get started with courses' : 'Choose your plan'}</label>
              <div className="grid grid-cols-2 gap-2">
                {PLAN_OPTIONS.map(function (opt) {
                  var active = selectedPlan === opt.id;
                  return (
                    <button type="button" key={opt.id} onClick={function () { setSelectedPlan(opt.id); }}
                      className={'relative text-left p-3 rounded-lg border transition-all ' + (active
                        ? 'border-[#2D2560] bg-[#EEEFF8] ring-2 ring-[#5A4E9E]/30'
                        : 'border-[#DDE1EE] bg-white hover:border-[#A8AAD4]')}
                      data-testid={'plan-option-' + opt.id}>
                      {active && (
                        <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-[#2D2560] flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        </span>
                      )}
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-sm font-bold text-[#1A1738]">{opt.label}</span>
                        <span className="text-xs text-[#6B6690]">{opt.price}{opt.paid ? '/mo' : ''}</span>
                      </div>
                      <p className="text-[11px] text-[#3A3566] leading-snug mt-1">{opt.blurb}</p>
                    </button>
                  );
                })}
              </div>
              {(function () {
                var opt = PLAN_OPTIONS.find(function (p) { return p.id === selectedPlan; });
                return opt && opt.paid ? (
                  <p className="text-xs text-[#2D2560] mt-2 font-medium">After signup we&rsquo;ll redirect you to checkout to activate your {opt.label} plan (7-day trial included).</p>
                ) : (
                  <p className="text-xs text-[#6B6690] mt-2">You can upgrade any time from Billing.</p>
                );
              })()}
            </div>

            <div data-testid="signup-name-field">
              <label className="block text-sm text-[#3A3566] font-medium mb-2">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8C86C4]" />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] placeholder:text-[#A8AAD4] focus:ring-2 focus:ring-[#5A4E9E] focus:border-[#5A4E9E] rounded-lg pl-11 pr-4 py-3 outline-none transition-all"
                  placeholder="John Doe" required data-testid="signup-name-input" />
              </div>
            </div>

            <div data-testid="signup-email-field">
              <label className="block text-sm text-[#3A3566] font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8C86C4]" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] placeholder:text-[#A8AAD4] focus:ring-2 focus:ring-[#5A4E9E] focus:border-[#5A4E9E] rounded-lg pl-11 pr-4 py-3 outline-none transition-all"
                  placeholder="you@example.com" required data-testid="signup-email-input" />
              </div>
            </div>

            <div data-testid="signup-phone-field">
              <label className="block text-sm text-[#3A3566] font-medium mb-2">Phone (Optional)</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8C86C4]" />
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] placeholder:text-[#A8AAD4] focus:ring-2 focus:ring-[#5A4E9E] focus:border-[#5A4E9E] rounded-lg pl-11 pr-4 py-3 outline-none transition-all"
                  placeholder="+1 (555) 123-4567" data-testid="signup-phone-input" />
              </div>
            </div>

            <div data-testid="signup-password-field">
              <label className="block text-sm text-[#3A3566] font-medium mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8C86C4]" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-[#DDE1EE] text-[#1E1A3E] placeholder:text-[#A8AAD4] focus:ring-2 focus:ring-[#5A4E9E] focus:border-[#5A4E9E] rounded-lg pl-11 pr-4 py-3 outline-none transition-all"
                  placeholder="••••••••" required data-testid="signup-password-input" />
              </div>
            </div>

            {/* ToS Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer" data-testid="tos-signup-checkbox-label">
              <input type="checkbox" checked={tosAccepted} readOnly
                onClick={function () { if (!tosAccepted) setTosOpen(true); else setTosAccepted(false); }}
                className="mt-0.5 w-4 h-4 rounded border-[#DDE1EE] bg-white text-[#5A4E9E] focus:ring-[#5A4E9E]"
                data-testid="tos-signup-checkbox" />
              <span className="text-xs text-[#6B6690] leading-relaxed">
                I agree to the{' '}
                <button type="button" onClick={function (e) { e.preventDefault(); setTosOpen(true); }} className="text-[#2D2560] hover:text-[#5A4E9E] underline" data-testid="tos-open-link">
                  Terms of Service, Privacy Policy & Arbitration Agreement
                </button>
              </span>
            </label>

            {error && (
              <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] rounded-lg p-3 text-sm" data-testid="signup-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading || !tosAccepted}
              className="w-full nebula-gradient cta-glow font-semibold tracking-wide rounded-lg py-3 transition-opacity hover:opacity-95 disabled:opacity-50"
              data-testid="signup-submit-button">
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#DDE1EE]"></div></div>
            <div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-[#6B6690]">Or continue with</span></div>
          </div>

          <button onClick={handleGoogleSignup}
            className="w-full flex items-center justify-center gap-3 bg-white border border-[#DDE1EE] text-[#1E1A3E] hover:bg-[#F6F7FC] font-semibold rounded-lg py-3 transition-colors"
            data-testid="google-signup-button">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign up with Google
          </button>

          <p className="text-center text-[#6B6690] text-sm mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2D2560] hover:text-[#5A4E9E] font-semibold transition-colors" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>

      <TosModal open={tosOpen} onClose={function () { setTosOpen(false); }} onAccept={handleTosAccept} mode="signup" />
    </div>
  );
};

export default Signup;
